import { ComponentType, lazy, LazyExoticComponent } from 'react';

import MLALogger from './logger';

// Records when we last reloaded because of a chunk failure, so a failure straight after that
// reload surfaces the error (to the ErrorBoundary) instead of reloading in a loop.
export const CHUNK_RELOAD_STORAGE_KEY = 'envd.chunkReloadAt';
export const CHUNK_RELOAD_GUARD_MS = 30 * 1000;
const RETRY_DELAY_MS = 1000;

interface LoadWithRetryOptions {
    retryDelayMs?: number;
    reload?: () => void;
}

/**
 * True for webpack's JS chunk failures (`ChunkLoadError`, both the `error:` and `missing:` variants)
 * and mini-css-extract's `Loading CSS chunk … failed`.
 */
export const isChunkLoadError = (error: unknown): boolean =>
    error instanceof Error &&
    (error.name === 'ChunkLoadError' || /Loading (CSS )?chunk \S+ failed/i.test(error.message));

/**
 * True when the chunk response arrived but wasn't the chunk, e.g. CloudFront's `index.html` fallback for a
 * file a deploy deleted. Retrying the same URL can't help, so we go straight to a reload.
 */
export const isMissingChunkError = (error: unknown): boolean =>
    isChunkLoadError(error) && /\(missing: /.test((error as Error).message);

const lastChunkReloadAt = (): number => {
    try {
        return Number(window.sessionStorage.getItem(CHUNK_RELOAD_STORAGE_KEY)) || 0;
    } catch {
        return 0;
    }
};

const markChunkReload = (): boolean => {
    try {
        window.sessionStorage.setItem(CHUNK_RELOAD_STORAGE_KEY, String(Date.now()));
        return true;
    } catch {
        // Without storage we can't guard against a reload loop, so don't reload at all.
        return false;
    }
};

const isOffline = (): boolean => typeof navigator !== 'undefined' && navigator.onLine === false;

/**
 * Runs a dynamic import, recovering from the two real-world causes of ChunkLoadError:
 * - a network blip (the chunk exists, `error:` variant): retry once after a short delay, then reload;
 * - a stale tab after a deploy (the chunk was deleted from S3, `missing:` variant): reload straight away
 *   to pick up the new build.
 * Anything else, or a failure we can't recover from, is rethrown for the ErrorBoundary.
 */
export const loadWithRetry = async <T>(
    factory: () => Promise<T>,
    { retryDelayMs = RETRY_DELAY_MS, reload = () => window.location.reload() }: LoadWithRetryOptions = {}
): Promise<T> => {
    let chunkError: unknown;
    try {
        return await factory();
    } catch (error) {
        if (!isChunkLoadError(error)) {
            throw error;
        }
        chunkError = error;
    }

    if (!isMissingChunkError(chunkError)) {
        await new Promise((resolve) => setTimeout(resolve, retryDelayMs));
        try {
            return await factory();
        } catch (error) {
            if (!isChunkLoadError(error)) {
                throw error;
            }
            chunkError = error;
        }
    }

    const recentlyReloaded = Date.now() - lastChunkReloadAt() < CHUNK_RELOAD_GUARD_MS;
    // Offline, a reload would only swap our page for the browser's offline page.
    if (isOffline() || recentlyReloaded || !markChunkReload()) {
        throw chunkError;
    }

    MLALogger.Log(['lazyWithRetry'], 'Chunk could not be loaded; reloading for the latest build', 'warn');
    reload();
    // Never settle, so Suspense keeps its fallback up while the page reloads.
    return new Promise<T>(() => undefined);
};

/** Drop-in replacement for `React.lazy` that recovers from chunk load failures. */
export const lazyWithRetry = <T extends ComponentType<any>>(
    factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> => lazy(() => loadWithRetry(factory));
